/* eslint-disable complexity */
/* eslint-disable max-lines */
import { NgIf } from '@angular/common';
import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  BookWidgetSearchModalMachCoreComponent,
  RecentListItem
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  GenericAction,
  MultiRoutePickerError,
  PredictiveSearch,
  RoutePicker,
  RoutePickerCities,
  RoutePickerError,
  SelectedData,
  Tab
} from '@com-delta-omnichannel/mach';
import { FromAndTo } from '@com-delta-omnichannel/mach-core-js';
import {
  PredictiveSearchItemSelectionEventData,
  SearchModalEvent,
  SearchModalEventType,
  TriggerLocation
} from '../../../../models/search-modal-events-object.interface';
import { ErrorStateDirective } from '../../directive';
import { focusRoutepicker } from '../mach-core-utils';

@Component({
  standalone: true,
  selector: 'idp-one-way-route-picker-mach-core',
  templateUrl: './one-way-route-picker-mach-core.component.html',
  imports: [BookWidgetSearchModalMachCoreComponent, ErrorStateDirective, NgIf]
})
export class OneWayRoutePickerMachCoreComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  private fromAndToInstance!: FromAndTo;
  private lastSearchModalTriggerLocation: TriggerLocation | undefined;
  protected readonly elementId = 'one-way-route-picker';
  protected modalOpen = false;

  @Input() routePickerContent!: RoutePicker;
  @Input() routePickerCities!: RoutePickerCities;
  @Input() recentSearchList: RecentListItem[] = [];
  @Input() savedSearchList: string[] = [];
  @Input() searchModalTabs: Tab[] = [];
  @Input() predictiveSearchData!: PredictiveSearch;
  @Input() isFullscreen = false;
  @Input() routePickerError:
    | RoutePickerError[]
    | MultiRoutePickerError[]
    | undefined = [];

  @Output() swapClicked = new EventEmitter<void>();
  @Output() searchModalTriggerClicked = new EventEmitter<SelectedData>();

  @Output() searchModalEvent = new EventEmitter<
    SearchModalEvent<SearchModalEventType>
  >();

  errorMessage = '';

  private getOriginTrigger() {
    const {
      origin: { title = '', subtitle = '' }
    } = this.routePickerContent;

    return {
      placeholder: subtitle,
      title: title
    };
  }

  private getDestinationTrigger() {
    const {
      destination: { title = '', subtitle = '' }
    } = this.routePickerContent;

    return {
      placeholder: subtitle,
      title: title
    };
  }

  private setupEventHandlers() {
    this.fromAndToInstance.onOriginClick(() => {
      const { originCode, originName, originRegion } = this.routePickerCities;
      this.handleRoutePickerClick({
        type: 'origin',
        selectedResult: {
          cityCode: originCode,
          cityName: originName,
          region: originRegion
        }
      });
    });

    this.fromAndToInstance.onDestinationClick(() => {
      const { destinationCode, destinationName, destinationRegion } =
        this.routePickerCities;
      this.handleRoutePickerClick({
        type: 'destination',
        selectedResult: {
          cityCode: destinationCode,
          cityName: destinationName,
          region: destinationRegion
        }
      });
    });

    this.fromAndToInstance.onSwap(() => {
      this.swapClicked.emit();
    });
  }

  protected initializeMachCoreRoutePicker() {
    this.fromAndToInstance = new FromAndTo({
      originTrigger: this.getOriginTrigger(),
      destinationTrigger: this.getDestinationTrigger(),
      variant: 'default',
      id: this.elementId,
      showNativeError: false
    });

    this.setupEventHandlers();
    this.setRoutePickerErrorState();
    this.setAirports(this.routePickerCities);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.fromAndToInstance) {
      if (changes['routePickerCities']) {
        this.setAirports(this.routePickerCities);
      }

      if (changes['routePickerError']) {
        this.setRoutePickerErrorState();
      }
    }
  }

  private setAirports({
    originName,
    originCode,
    destinationName,
    destinationCode
  }: RoutePickerCities) {
    const {
      origin: { subtitle: originSubtitle = 'Origin' } = {},
      destination: { subtitle: destinationSubtitle = 'Destination' } = {}
    } = this.routePickerContent || {};

    if (originCode) {
      this.fromAndToInstance.setOrigin(
        originCode,
        originName || originSubtitle
      );
    }

    if (destinationCode) {
      this.fromAndToInstance.setDestination(
        destinationCode,
        destinationName || destinationSubtitle
      );
    }
  }

  ngAfterViewInit() {
    this.initializeMachCoreRoutePicker();
  }

  protected handleModalEvents(event: SearchModalEvent<SearchModalEventType>) {
    this.searchModalEvent.emit(event);
  }

  protected handleRoutePickerClick(data: SelectedData) {
    this.modalOpen = true;
    this.lastSearchModalTriggerLocation = data.type as TriggerLocation;
    this.searchModalEvent.emit({ type: 'modalOpen' });
    this.searchModalTriggerClicked.emit(data);
  }

  protected handleSearchModalClose(searchText: string) {
    if (searchText) {
      this.handlePredictiveSearchItemSelection({
        label: searchText.toUpperCase(),
        id: searchText.toUpperCase(),
        value: ''
      });
    }
    this.modalOpen = false;
    this.searchModalEvent.emit({ type: 'modalClose' });
    focusRoutepicker(
      this.lastSearchModalTriggerLocation,
      this.fromAndToInstance
    );
  }

  protected handlePredictiveSearchItemSelection(
    eventData: GenericAction<string>
  ) {
    const data: PredictiveSearchItemSelectionEventData = {
      airportCode: eventData.id,
      airportCity: eventData.value ?? '',
      locationType: this.lastSearchModalTriggerLocation as TriggerLocation,
      flightIndex: 0
    };
    this.searchModalEvent.emit({
      type: 'predictiveSearchItemSelected',
      data
    });
  }

  private setRoutePickerErrorState() {
    this.errorMessage = '';
    if (this.fromAndToInstance) {
      this.fromAndToInstance.setOriginError(false);
      this.fromAndToInstance.setDestinationError(false);
    }

    if (this.routePickerError?.[0] && this.fromAndToInstance) {
      const error = this.routePickerError[0] as RoutePickerError;
      const {
        origin,
        destination,
        sameRoute,
        originErrorMessage,
        destinationErrorMessage,
        sameRouteErrorMessage,
        bothFieldsErrorMessage
      } = error;

      if (sameRoute) {
        this.fromAndToInstance.setDestinationError(true);
        this.errorMessage = sameRouteErrorMessage;
      } else if (origin) {
        this.fromAndToInstance.setOriginError(true);
        this.errorMessage = originErrorMessage;

        if (destination) {
          this.fromAndToInstance.setDestinationError(true);
          this.errorMessage = bothFieldsErrorMessage;
        }
      } else if (destination) {
        this.fromAndToInstance.setDestinationError(true);
        this.errorMessage = destinationErrorMessage;
      }
    }
  }

  ngOnDestroy(): void {
    if (this.fromAndToInstance) {
      this.fromAndToInstance.destroy();
    }
  }
}
